import { useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { createTableHook, tableFeatures } from "@tanstack/react-table";
import * as stylex from "@stylexjs/stylex";
import { queries } from "../lib/api";
import { styles } from "../lib/styles";
import { historySearchSchema, type Recording } from "../lib/schemas";
import { RequestFailure } from "../components/RequestFailure";
import { HistoryControls } from "../components/HistoryControls";
import {
  NavIcon,
  PageHeader,
  SearchField,
  ResultStatus,
  TableScroll,
} from "../components/Workspace";

export const Route = createFileRoute("/recordings/")({
  validateSearch: historySearchSchema,
  component: RecordingsPage,
});
const { useAppTable, createAppColumnHelper } = createTableHook({ features: tableFeatures({}) });
const helper = createAppColumnHelper<Recording>();
function RecordingsPage() {
  const filters = Route.useSearch();
  const navigate = Route.useNavigate();
  const me = useQuery(queries.me());
  const list = useQuery({
    ...queries.recordings({ q: filters.q, status: filters.status, cursor: filters.cursor }),
    enabled: me.data?.roles.includes("auditor") === true,
  });
  const change = (value: Partial<typeof filters>) => {
    void navigate({ search: (current) => ({ ...current, ...value }), replace: true });
  };
  const columns = useMemo(
    () => [
      helper.display({
        id: "when",
        header: "Started",
        cell: ({ row }) => new Date(row.original.created_at).toLocaleString(),
      }),
      helper.display({
        id: "server",
        header: "Server / account",
        cell: ({ row }) => `${row.original.alias} / ${row.original.login}`,
      }),
      helper.display({ id: "who", header: "Person", cell: ({ row }) => row.original.email }),
      helper.display({ id: "kind", header: "Channel", cell: ({ row }) => row.original.kind }),
      helper.display({
        id: "state",
        header: "State",
        cell: ({ row }) => (
          <span
            {...stylex.props(styles.status, row.original.status === "complete" && styles.applied)}
          >
            {row.original.status}
          </span>
        ),
      }),
      helper.display({
        id: "action",
        header: "Review",
        cell: ({ row }) =>
          row.original.status === "recording" ? (
            <span {...stylex.props(styles.small)}>In progress</span>
          ) : (
            <Link
              className="action-link"
              to="/recordings/$recordingId"
              params={{ recordingId: row.original.id }}
              search={filters}
            >
              Replay <NavIcon name="ArrowRight" />
            </Link>
          ),
      }),
    ],
    [filters],
  );
  const data = list.data?.items ?? [];
  const table = useAppTable({ columns, data, getRowId: (row) => row.id });
  if (me.isPending)
    return (
      <p aria-busy="true" {...stylex.props(styles.notice)}>
        Checking recording permissions…
      </p>
    );
  if (!me.data?.roles.includes("auditor"))
    return (
      <p role="alert" {...stylex.props(styles.error)}>
        Auditor access is required.
      </p>
    );
  return (
    <>
      <PageHeader
        title="Session recordings"
        description="Replay managed SSH output. Encrypted recordings are retained for 30 days."
      />
      {list.data && !list.data.storage.healthy && (
        <p role="alert" {...stylex.props(styles.error)}>
          Recording storage needs attention. New managed SSH access is blocked until it is healthy.
        </p>
      )}
      <RequestFailure
        error={list.error}
        retry={() => {
          void list.refetch();
        }}
        pending={list.isFetching}
      />
      <section data-workspace-panel {...stylex.props(styles.panel)}>
        <div className="table-toolbar">
          <SearchField
            label="Search recordings"
            value={filters.q ?? ""}
            onChange={(q) => change({ q, cursor: undefined })}
            placeholder="Search server, person or account…"
          />
          <label {...stylex.props(styles.field)}>
            State
            <select
              {...stylex.props(styles.select)}
              value={filters.status ?? "all"}
              onChange={(event) =>
                change({
                  status: historySearchSchema.shape.status.parse(event.target.value),
                  cursor: undefined,
                })
              }
            >
              <option value="all">All recordings</option>
              <option value="complete">Complete</option>
              <option value="recording">In progress</option>
              <option value="interrupted">Interrupted</option>
              <option value="failed">Failed</option>
            </select>
          </label>
        </div>
        <TableScroll label="Recordings table">
          <table {...stylex.props(styles.table)}>
            <thead>
              {table.getHeaderGroups().map((group) => (
                <tr key={group.id}>
                  {group.headers.map((header) => (
                    <th key={header.id} {...stylex.props(styles.th)}>
                      <table.FlexRender header={header} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getAllCells().map((cell) => (
                    <td key={cell.id} {...stylex.props(styles.td)}>
                      <table.FlexRender cell={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
        {list.isSuccess && !list.isFetching && data.length === 0 && (
          <ResultStatus>
            No matching recordings within the retention window. Try a different search or state
            filter.
          </ResultStatus>
        )}
        {list.isPending && !list.error && (
          <p aria-busy="true" {...stylex.props(styles.empty)}>
            Loading recordings…
          </p>
        )}
        <HistoryControls
          count={list.data ? data.length : undefined}
          noun="recordings"
          next={list.data?.page.next_cursor}
          previous={list.data?.page.previous_cursor}
          cursor={filters.cursor}
          busy={list.isFetching}
          onPage={(cursor) => change({ cursor })}
        />
        <p {...stylex.props(styles.small)}>
          Search covers every retained recording. Replay and downloads are audited. Forwarded
          protocols and SFTP include activity metadata.
        </p>
      </section>
    </>
  );
}
